/**
 * Thin client wrapper around POST /api/dex/screenshot-batch.
 *
 * Used by Inventory's BoxScan and PokeGenie list OCR confirm flows so a
 * screenshot ingest run is durable on the server side: the next time the
 * user logs in their dex picks up from exactly where they left off.
 *
 * The server dedupes by SHA-256 of the raw bytes that produced the scan.
 * For multi-screenshot scans we hash a canonical concatenation of every
 * row's stable identity (species:cp:shiny) which is good enough to spot
 * an accidental re-confirm of the same set.
 */
import { apiRequest } from "./queryClient";
import { POKEMON } from "./pokemon";
import { SPECIES_NAMES } from "@/data/speciesNames";

// Full national-dex name -> id map (1..1025) merged with the curated POKEMON
// roster. The curated list is used everywhere else in the UI for art/glyphs;
// SPECIES_NAMES is the complete table used by the worker. We need BOTH so a
// real-world box scan (which surfaces species outside the curated 137) can
// still resolve to a numeric id and reach the worker. Previously every
// species not in POKEMON silently dropped at the speciesIdFromName step,
// which is how the b8 live scan reported "Caught 0" with 43 frames in.
let _nameToId: Map<string, number> | null = null;
function buildNameToId(): Map<string, number> {
  const m = new Map<string, number>();
  for (const [id, name] of Object.entries(SPECIES_NAMES)) {
    if (typeof name === "string" && name.length > 0) {
      m.set(name.trim().toLowerCase(), Number(id));
    }
  }
  // POKEMON wins on conflicts so the curated spelling stays canonical (it
  // matches the in-app glyph/art name).
  for (const p of POKEMON) {
    m.set(p.name.trim().toLowerCase(), p.id);
  }
  return m;
}
function nameToId(name: string): number | null {
  if (!name || typeof name !== "string") return null;
  if (!_nameToId) _nameToId = buildNameToId();
  return _nameToId.get(name.trim().toLowerCase()) ?? null;
}

export function speciesIdFromName(name: string | null | undefined, fallbackDexNumber?: number): number | null {
  if (fallbackDexNumber && fallbackDexNumber > 0) return fallbackDexNumber;
  if (typeof name !== "string") return null;
  return nameToId(name);
}

export type ScreenshotSource =
  | "box_scan"
  | "pokegenie_list"
  | "profile_ocr"
  | "detail_ocr"
  | "live_scan"
  // PR-F: new ingest paths
  | "pokedex_scan"
  | "journal_scan"
  | "video_frame";

export type CaptureMethod =
  | "wild" | "raid" | "egg" | "research" | "trade"
  | "gift" | "incense" | "lure" | "snapshot" | "unknown";

export type WeatherBoost =
  | "sunny" | "rainy" | "snowy" | "windy"
  | "foggy" | "partly_cloudy" | "cloudy";

export type SourceScreen =
  | "box_scan" | "detail_ocr" | "pokedex_scan"
  | "journal_scan" | "pokegenie_list" | "video_frame" | "manual";

export interface ScreenshotConfirmedRow {
  speciesId: number;
  formId?: string | null;
  isShiny?: boolean;
  isLucky?: boolean;
  isShadow?: boolean;
  isPurified?: boolean;
  sizeTag?: "xxs" | "xs" | "normal" | "xl" | "xxl" | "unknown";
  ivAtk?: number | null;
  ivDef?: number | null;
  ivSta?: number | null;
  cp?: number | null;
  hp?: number | null;
  level?: number | null;
  nickname?: string | null;
  caughtAt?: number | null;
  // PR-F additions per pogo_screen_ingest_spec.md §12.2. All optional so
  // existing callers (Box Scan, PokeGenie list) keep working unchanged.
  catchLocation?: string | null;
  catchDate?: number | null;
  captureMethod?: CaptureMethod | null;
  tradePartner?: string | null;
  weatherBoost?: WeatherBoost | null;
  sourceScreen?: SourceScreen | null;
  pokegenieRank?: number | null;
}

export interface ScreenshotReviewRow {
  speciesId?: number | null;
  speciesName?: string | null;
  cp?: number | null;
  isShiny?: boolean;
  spriteDataUrl?: string | null;
  nicknameRaw?: string | null;
  ocrWarnings?: string[];
  confidence?: number | null;
}

export interface ScreenshotBatchResult {
  batchId: number;
  accepted: number;
  queued: number;
  deduped: boolean;
}

export async function sha256OfString(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input);
  // crypto.subtle is available in modern browsers and the WKWebView used
  // by Capacitor. We never call this in node, so no polyfill needed.
  const digest = await crypto.subtle.digest("SHA-256", enc);
  const bytes = Array.from(new Uint8Array(digest));
  return bytes.map((b) => b.toString(16).padStart(2, "0")).join("");
}


